// Byte/hex helpers for the Security Access Seed-Key Simulator. Kept separate from
// algorithms.js so hex formatting/parsing can be reused (and tested) independently
// of which training algorithm is selected.

export const toUint32 = (n) => n >>> 0;

// Big-endian 4-byte hex breakdown of a 32-bit value, e.g. 0x12345678 -> ['12','34','56','78'].
export const toHexBytes = (value) => {
  const v = toUint32(value);
  return [24, 16, 8, 0].map((shift) => ((v >>> shift) & 0xff).toString(16).toUpperCase().padStart(2, '0'));
};

export const bytesToHexString = (bytes) => bytes.join(' ');

export const toHex32String = (value) => bytesToHexString(toHexBytes(value));

// Parses "0x1A2B3C4D", "1A2B3C4D", or space-separated byte pairs ("1A 2B 3C 4D") into a
// uint32. Returns null (not a thrown error) for anything that isn't a valid 32-bit hex
// value, so callers can drive inline validation UI off the null check.
export const parseHex32 = (input) => {
  if (typeof input !== 'string') return null;
  const cleaned = input.trim().replace(/^0x/i, '').replace(/\s+/g, '');
  if (!/^[0-9a-fA-F]{1,8}$/.test(cleaned)) return null;
  return parseInt(cleaned, 16) >>> 0;
};

export const rotl32 = (value, n) => {
  const shift = ((n % 32) + 32) % 32;
  const v = toUint32(value);
  if (shift === 0) return v;
  return toUint32((v << shift) | (v >>> (32 - shift)));
};

// Standard CRC-32 (IEEE 802.3 / zlib polynomial), computed over an array of byte values.
// Table is built lazily once and cached at module scope.
let crcTable = null;
const getCrcTable = () => {
  if (crcTable) return crcTable;
  crcTable = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : c >>> 1;
    }
    crcTable[i] = c >>> 0;
  }
  return crcTable;
};

export const crc32 = (bytes) => {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

// Cryptographically-irrelevant pseudo-random 32-bit seed generator — this is a training
// tool, not a real ECU, so `Math.random` is fine here (Web Crypto would be misleading
// overkill for a fictional teaching algorithm).
export const randomSeed32 = () => {
  const hi = Math.floor(Math.random() * 0x10000);
  const lo = Math.floor(Math.random() * 0x10000);
  return toUint32((hi << 16) | lo);
};
